import { Capacitor } from '@capacitor/core';
import { isNativeApp } from './platform.js';
import { API_URL as API } from '../config/env.js';
import { goNativeTab } from '../mobile/NativeTabContext';
import { OPEN_DAILY_PAPER_EVENT } from './paperCache';
import { getEditionAlertsEnabled } from './editionAlerts.js';

let listenersReady = false;
let lastPostedToken = '';
let lastAuthToken = '';

async function postDevice(token, authToken) {
  if (!token) return;
  try {
    await fetch(`${API}/api/devices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({
        token,
        platform: Capacitor.getPlatform(),
      }),
    });
    lastPostedToken = token;
    lastAuthToken = authToken || '';
  } catch {
    /* offline / server down */
  }
}

async function deleteDevice(token) {
  if (!token) return;
  try {
    await fetch(`${API}/api/devices`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
  } catch {
    /* ignore */
  }
}

async function ensureListeners(PushNotifications) {
  if (listenersReady) return;
  listenersReady = true;

  PushNotifications.addListener('registration', (ev) => {
    void postDevice(ev.value, lastAuthToken);
  });

  PushNotifications.addListener('registrationError', () => {
    /* Simulator often has no APNs — ignore */
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (ev) => {
    const dateKey = ev?.notification?.data?.dateKey || ev?.notification?.data?.date;
    goNativeTab('paper');
    if (dateKey) {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent(OPEN_DAILY_PAPER_EVENT, { detail: { dateKey } }));
      }, 80);
    }
  });
}

/**
 * Register for APNs via Capacitor. Safe to call repeatedly.
 * Pass authToken when logged in so the device is linked to the user.
 */
export async function initPushNotifications(authToken) {
  if (!isNativeApp()) return false;

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    lastAuthToken = authToken || lastAuthToken || '';

    await ensureListeners(PushNotifications);

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return false;

    await PushNotifications.register();
    return true;
  } catch {
    return false;
  }
}

/** Re-send token after login with auth header. */
export async function refreshPushRegistration(authToken) {
  if (!isNativeApp()) return;
  if (!getEditionAlertsEnabled()) return;
  lastAuthToken = authToken || '';
  if (lastPostedToken && authToken) {
    await postDevice(lastPostedToken, authToken);
  }
  await initPushNotifications(authToken);
}

/** Opt out: remove this device from the server push list. */
export async function unregisterPushNotifications() {
  if (!isNativeApp()) return;
  if (lastPostedToken) {
    await deleteDevice(lastPostedToken);
    lastPostedToken = '';
  }
}
